import type { ColumnSizingState, Table } from '@tanstack/react-table';

export type DataTableAlign = 'left' | 'center' | 'right';
export type DataTableSticky = 'left' | 'right';
export type DataTableDensity = 'comfortable' | 'compact';
export type ScoreBand = 'high' | 'good' | 'fair' | 'low' | 'none';

/**
 * Per-column presentation + export metadata. Attached to a column via `meta`.
 */
export interface DataTableColumnMeta<TData = unknown> {
  width?: string;
  align?: DataTableAlign;
  sticky?: DataTableSticky;
  headerClassName?: string;
  cellClassName?: string;
  /** Set false to omit the column from CSV export (default: included for accessor columns). */
  exportable?: boolean;
  /** Custom CSV value for columns whose cell renderer is not a plain value. */
  exportValue?: (row: TData) => string | number | null | undefined;
  /**
   * Human-readable CSV header for columns whose `header` is a render function
   * (e.g. a sortable SortHeader), where the text can't be derived — otherwise the
   * export falls back to the lowercase column id.
   */
  exportHeader?: string;
  /**
   * Human label for the sort control's accessible name when `header` is a render
   * function. Without it the sort button falls back to the column id (e.g.
   * "Sort by lastActive"); set it so keyboard/screen-reader users hear
   * "Sort by <label>".
   */
  headerSortLabel?: string;
  /**
   * Definition for the column, shown as a visible (i) next to the header label
   * and exposed as (a) an always-on `aria-describedby` target for assistive tech
   * and (b) a focus/hover Radix tooltip on the sort button (or, for an unsortable
   * column, on the focusable label) — announced to screen readers and visible to
   * keyboard users on focus, not hover-only.
   */
  headerDescription?: string;
}

/** State of a collection-view row that can open a peek/detail drawer. */
export interface DrawerRowState {
  /** The row whose peek drawer is currently open (`?peek=<id>`). */
  active?: boolean;
  /** The row is checkbox-selected for a bulk action. */
  selected?: boolean;
  /**
   * Tri-state, only meaningful for a bare `<Table>` row (the DataTable computes
   * its own hover): `true` adds the resting hover affordance; `false` cancels the
   * shadcn `<TableRow>` default hover so a non-clickable row shows none; leave
   * UNSET inside a DataTable `rowClassName` callback so the primitive owns its
   * frozen-aware default.
   */
  interactive?: boolean;
}

/**
 * Canonical background classes for a collection-view row that can open a peek
 * drawer — the single source of truth shared by the `DataTable` primitive (via a
 * `rowClassName` callback) and by bespoke `<Table>` surfaces (e.g. Suppliers), so
 * the selected-row + hover highlight render identically everywhere.
 *
 * Precedence: an open drawer (`active`) beats a bulk `selected`, which beats the
 * resting hover affordance. The highlight tokens are SOLID (`bg-muted`,
 * `bg-accent`) so frozen/sticky cells that reuse the class stay opaque and
 * composite the same as scrolling cells (see `VirtualizedMarketplaceTable`).
 *
 * For a raw `<Table>` row pass `interactive` explicitly: `true` yields the
 * resting `hover:bg-muted/50` affordance (the DataTable's own non-frozen
 * default); `false` yields `hover:bg-transparent`, which cancels the shadcn
 * `<TableRow>` baked-in `hover:bg-muted/50` so a non-clickable row shows no click
 * cue. Leave `interactive` UNSET inside a `DataTable` `rowClassName` callback:
 * returning `''` there lets the primitive apply its frozen-aware
 * `bg-background hover:bg-muted` default, whereas a literal hover class would be
 * taken verbatim and suppress the opaque frozen background.
 */
export function drawerRowStateClasses({ active, selected, interactive }: DrawerRowState): string {
  // Highlighted rows pin a same-colour hover so the solid highlight survives the
  // ambient `hover:bg-muted/50` a bare shadcn `<TableRow>` bakes in; on the
  // DataTable path (its own row div) the extra class is the same tone, a no-op.
  if (active) {
    return 'bg-muted hover:bg-muted';
  }
  if (selected) {
    return 'bg-accent hover:bg-accent';
  }
  if (interactive === true) {
    return 'hover:bg-muted/50';
  }
  if (interactive === false) {
    return 'hover:bg-transparent';
  }
  return '';
}

const SYNTHETIC_COLUMN_IDS = new Set(['__select__', '__expander__', '__tree__']);

export function isSyntheticColumn(id: string): boolean {
  return SYNTHETIC_COLUMN_IDS.has(id);
}

export function getAlignmentClassName(align: DataTableAlign = 'left'): string {
  switch (align) {
    case 'center':
      return 'justify-center text-center';
    case 'right':
      return 'justify-end text-right';
    default:
      return 'justify-start text-left';
  }
}

// Position + z-index only. The opaque background is applied by the caller so it can
// match its row: white (bg-background) in the body, the frosted muted tone in the header.
export function getStickyClassName(sticky?: DataTableSticky): string {
  if (sticky === 'left') {
    return 'sticky left-0 z-20';
  }
  if (sticky === 'right') {
    return 'sticky right-0 z-20';
  }
  return '';
}

export function getColumnMeta<TData>(columnDef: { meta?: unknown }): DataTableColumnMeta<TData> | undefined {
  return columnDef.meta as DataTableColumnMeta<TData> | undefined;
}

export function getColumnWidth(column: { columnDef: { meta?: unknown } }): string {
  return getColumnMeta(column.columnDef)?.width ?? 'minmax(140px, 1fr)';
}

/**
 * Minimum pixel width for a grid-column spec, used to size the table's
 * horizontal-scroll min-width on small screens. `minmax(220px, 2fr)` → 220,
 * `140px` → 140, fr/auto/other → 96 (a sane minimum so columns never crush).
 */
export function minColumnWidth(spec: string): number {
  const match = spec.match(/(\d+(?:\.\d+)?)px/);
  return match ? Number(match[1]) : 96;
}

export function toCssSize(value: number | string): string {
  return typeof value === 'number' ? `${value}px` : value;
}

export function getRowAccessibleLabel(row: Record<string, unknown>, fallback: string): string {
  const candidate = row.name ?? row.customer ?? row.supplier ?? row.contract ?? row.company ?? row.title;
  return typeof candidate === 'string' && candidate.trim().length > 0 ? candidate : fallback;
}

export function headerToText(header: unknown, fallback: string): string {
  return typeof header === 'string' && header.length > 0 ? header : fallback;
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return '?';
  }
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function scoreBand(score: number | null | undefined, max = 100): ScoreBand {
  if (score === null || score === undefined || Number.isNaN(score)) {
    return 'none';
  }
  const pct = max > 0 ? (score / max) * 100 : 0;
  if (pct >= 80) {
    return 'high';
  }
  if (pct >= 60) {
    return 'good';
  }
  if (pct >= 40) {
    return 'fair';
  }
  return 'low';
}

/**
 * RFC-4180-ish escaping for a single CSV field, with CSV-injection mitigation:
 * string values that begin with a formula trigger (`=`, `+`, `-`, `@`, tab, CR) are
 * prefixed with a single quote so spreadsheet apps treat them as text, never formulas.
 * Numeric values are inherently safe and are never prefixed.
 */
export function csvField(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  let text = String(value);
  if (typeof value !== 'number' && /^[=+\-@\t\r]/.test(text)) {
    text = `'${text}`;
  }
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }
  return text;
}

/** Export-only CSV column (e.g. the row UUID) — included in the file, never displayed. */
export interface CsvExtraColumn<TData> {
  header: string;
  value: (row: TData) => string | number | null | undefined;
}

/**
 * Build a CSV string from the table's filtered **and sorted** rows + visible
 * exportable columns. Reads the sorted (pre-pagination) row model so the export
 * mirrors the order the user is looking at after sorting a column — not the raw
 * input order. In server mode (no sorted/filtered model configured) the getter
 * falls back through the TanStack pipeline to the core row model, i.e. the rows
 * the read-model already returned in server-resolved order.
 * `extraColumns` are appended after the visible columns.
 */
export function buildCsv<TData>(
  table: Table<TData>,
  extraColumns: CsvExtraColumn<TData>[] = [],
  // When provided, export THIS full set (e.g. all filtered rows fetched for export)
  // instead of the table's current (paginated) page. Column mapping is reused; a
  // column without `exportValue` emits blank for override rows (TanStack's per-row
  // `getValue` isn't available for raw data — server-paginated exports define
  // `exportValue` on every exported column).
  explicitRows?: readonly TData[],
): string {
  const columns = table
    .getVisibleLeafColumns()
    .filter((column) => !isSyntheticColumn(column.id) && getColumnMeta(column.columnDef)?.exportable !== false);

  const header = [
    ...columns.map((column) =>
      csvField(
        getColumnMeta(column.columnDef)?.exportHeader ??
          headerToText(column.columnDef.header, column.id),
      ),
    ),
    ...extraColumns.map((extra) => csvField(extra.header)),
  ];

  const buildRow = (original: TData, getValue?: (columnId: string) => unknown) => [
    ...columns.map((column) => {
      const meta = getColumnMeta<TData>(column.columnDef);
      if (meta?.exportValue) {
        return csvField(meta.exportValue(original));
      }
      return csvField(getValue ? getValue(column.id) : '');
    }),
    ...extraColumns.map((extra) => csvField(extra.value(original))),
  ];

  const rows = explicitRows
    ? explicitRows.map((original) => buildRow(original))
    : table.getSortedRowModel().rows.map((row) => buildRow(row.original, (id) => row.getValue(id)));

  return [header, ...rows].map((line) => line.join(',')).join('\r\n');
}

const COLUMN_SIZING_STORAGE_PREFIX = 'sg-data-table-column-sizing:';

/**
 * Load persisted column widths for a resizable table. Returns {} when the key is
 * unset, storage is unavailable (SSR/tests), or the stored value is malformed —
 * a bad value degrades to the column-meta defaults, never throws.
 */
export function loadColumnSizing(storageKey: string | undefined): ColumnSizingState {
  if (!storageKey) {
    return {};
  }
  try {
    const raw = window.localStorage.getItem(`${COLUMN_SIZING_STORAGE_PREFIX}${storageKey}`);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const sizing: ColumnSizingState = {};
    for (const [columnId, size] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof size === 'number' && Number.isFinite(size) && size > 0) {
        sizing[columnId] = size;
      }
    }
    return sizing;
  } catch {
    return {};
  }
}

const COLUMN_ORDER_STORAGE_PREFIX = 'sg-data-table-column-order:';

/** Load a persisted column order. Returns [] when unset/unavailable/malformed. */
export function loadColumnOrder(storageKey: string | undefined): string[] {
  if (!storageKey) {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(`${COLUMN_ORDER_STORAGE_PREFIX}${storageKey}`);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

/** Persist a column order. No-ops (never throws) when storage is unavailable. */
export function persistColumnOrder(storageKey: string | undefined, order: string[]): void {
  if (!storageKey) {
    return;
  }
  try {
    const key = `${COLUMN_ORDER_STORAGE_PREFIX}${storageKey}`;
    if (order.length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(order));
  } catch {
    // Storage full/blocked — reordering still works for the session.
  }
}

/**
 * Reconcile a saved column order against the table's actual leaf columns.
 * Pinned columns — synthetic (select/expander/tree) plus any ids passed in
 * `pinnedIds` (e.g. sticky columns, which must stay in their frozen region) —
 * keep their default positions and are immune to saved orders. The remaining
 * columns keep their saved order, with new/unsaved columns appended in default
 * order. Safe against renamed or removed columns.
 */
export function sanitizeColumnOrder(saved: string[], allIds: string[], pinnedIds: string[] = []): string[] {
  const pinned = new Set([...allIds.filter((id) => isSyntheticColumn(id)), ...pinnedIds]);
  const kept = saved.filter((id) => allIds.includes(id) && !pinned.has(id));
  const missing = allIds.filter((id) => !pinned.has(id) && !kept.includes(id));
  const flowing = [...kept, ...missing];
  let cursor = 0;
  return allIds.map((id) => (pinned.has(id) ? id : flowing[cursor++]));
}

/** Persist column widths for a resizable table. No-ops (never throws) when storage is unavailable. */
export function persistColumnSizing(storageKey: string | undefined, sizing: ColumnSizingState): void {
  if (!storageKey) {
    return;
  }
  try {
    const key = `${COLUMN_SIZING_STORAGE_PREFIX}${storageKey}`;
    if (Object.keys(sizing).length === 0) {
      window.localStorage.removeItem(key);
      return;
    }
    window.localStorage.setItem(key, JSON.stringify(sizing));
  } catch {
    // Storage full/blocked — resizing still works for the session, just not remembered.
  }
}

// (intentionally no other helpers — keep this module to functions the component uses)

/** Trigger a client-side file download. Returns false when the browser cannot create an object URL. */
export function downloadTextFile(filename: string, text: string, mimeType: string): boolean {
  if (typeof URL?.createObjectURL !== 'function') {
    return false;
  }
  const blob = new Blob([text], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
  return true;
}

/** Trigger a client-side CSV download. Returns false when the browser cannot create an object URL. */
export function downloadCsv(filename: string, csv: string): boolean {
  return downloadTextFile(
    filename.endsWith('.csv') ? filename : `${filename}.csv`,
    csv,
    'text/csv;charset=utf-8;',
  );
}
